import { Input } from "antd";
import { InputProps } from "antd/es/input";
import classNames from "classnames";
import { FieldWrapper } from "components/UI/Table/UI/InteractiveCell/components/FieldWrapper.tsx";
import { WithSaveButton } from "components/UI/Table/UI/InteractiveCell/components/WithSaveButton.tsx";
import {
    useInteractiveCell,
    useSafeFormContext,
} from "components/UI/Table/UI/InteractiveCell/hooks/useInteractiveCell.ts";
import { AlwaysEditingFieldProps, FieldProps } from "components/UI/Table/UI/InteractiveCell/miscs/types.ts";
import { ChangeEvent, useEffect, useState } from "react";
import { FieldValues, Path } from "react-hook-form";

type Props<T extends FieldValues> = InputProps & FieldProps<T> & AlwaysEditingFieldProps;

export const WithInput = <T extends FieldValues = FieldValues>({ formControl, ...props }: Props<T>) => {
    const { value, className, onChange, onSave, alwaysEditing = false, ...restProps } = props;

    const {
        setControlledValue,
        handleOnSaveSideEffects,
        resolvedValue,
        isFormControlled,
        handleFocus,
        handleBlur,
        fieldRef,
        error,
    } = useInteractiveCell({
        formControl,
        value,
    });

    const formContext = useSafeFormContext<T>();
    const [draftValue, setDraftValue] = useState<string>("");

    useEffect(() => {
        setDraftValue((resolvedValue as string) ?? props.defaultValue ?? "");
    }, [resolvedValue, props.defaultValue]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setDraftValue(event.target.value);
        if (alwaysEditing && isFormControlled && formContext && formControl?.name) {
            formContext.setValue(formControl.name as Path<T>, event.target.value as never, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
        onChange?.(event);
    };

    const handleSave = () => {
        if (isFormControlled && formContext && formControl?.name) {
            formContext.setValue(formControl.name as Path<T>, draftValue as never, {
                shouldValidate: true,
                shouldDirty: true,
            });
        } else {
            setControlledValue(draftValue);
        }
        handleOnSaveSideEffects();
        onSave?.(draftValue);
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <FieldWrapper
            displayValue={resolvedValue || props.defaultValue}
            ref={fieldRef}
            isError={!!error}
            errorMessage={error?.message as string}
            overlay={props.overlay}
            alwaysEditing={alwaysEditing}
        >
            <WithSaveButton onSave={handleSave} hideSaveButton={alwaysEditing}>
                <Input
                    {...restProps}
                    className={classNames(className, "!w-full")}
                    value={draftValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onClick={handleClick}
                />
            </WithSaveButton>
        </FieldWrapper>
    );
};
